import { useAuth0 } from '@auth0/auth0-react'
import {
  type Theme,
  Button,
  ButtonGroup,
  DropdownMenu,
  Icon,
  Tooltip,
  useTheme,
} from '@dittolive/anvil'
import {
  ArrowUpRightIcon,
  MonitorIcon,
  MoonIcon,
  PlusIcon,
  SunIcon,
} from '@phosphor-icons/react'
import graphql from 'babel-plugin-relay/macro'
import paths from 'paths'
import { useContext, useState } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { NavLink } from 'react-router-dom'

import AppContext from '../../context'
import { SideNavUserMenuQuery } from './__generated__/SideNavUserMenuQuery.graphql'

const windowEnvironment = window._env_

const query = graphql`
  query SideNavUserMenuQuery {
    viewer {
      me {
        id
        firstName
        lastName
        imageUrl
        email
        hubspotContactId
        apps {
          # eslint-disable-next-line relay/unused-fields
          id
        }
        organizations {
          # eslint-disable-next-line relay/unused-fields
          id
          incomingAppRequests {
            __typename
          }
          ownershipChangeRequest {
            toMember {
              id
              user {
                id
              }
            }
          }
        }
        incomingAppRequests {
          __typename
        }
        incomingOrganizationInvitations {
          id
          rejectedAt
          acceptedAt
        }
      }
      organizations {
        totalCount
        edges {
          node {
            # eslint-disable-next-line relay/unused-fields
            id
            apps {
              # eslint-disable-next-line relay/unused-fields
              id
            }
          }
        }
      }
    }
  }
`

const triggerClass =
  'flex h-9 items-center gap-2 rounded-lg px-2.5 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none text-foreground-subtle hover:bg-fill-opaque hover:text-foreground-normal'

const itemIconClass =
  'size-4 text-foreground-subtle group-hover:text-foreground-normal group-focus-visible:text-foreground-normal'

const superAdminItemClass =
  'hover:bg-fill-promo-secondary-hovered focus:bg-fill-promo-secondary-hovered'

/**
 * Account menu for the SideNavLayout footer: a full-width sidebar-style
 * trigger with the user's avatar and name, opening a dropdown with
 * notifications, settings, theme, super admin links, and log out.
 */
const SideNavUserMenu = ({ collapsed = false }: { collapsed?: boolean }) => {
  const { isSuperAdmin } = useContext(AppContext)

  const { logout } = useAuth0()
  const { viewer } = useLazyLoadQuery<SideNavUserMenuQuery>(
    query,
    {},
    { fetchPolicy: 'store-and-network' },
  )
  const [userImageError, setUserImageError] = useState(false)

  // Gate tenant-creation links the same way the legacy NavigationBarRouter did:
  // visible to staff, or to any viewer with at least one app or organization.
  const viewerHasAtLeastOneApp =
    !!viewer.me?.apps.length ||
    (!!viewer.organizations.totalCount &&
      !!viewer.organizations.edges?.length &&
      !!viewer.organizations.edges.reduce(
        (totalApps, edge) => totalApps + (edge?.node?.apps.length || 0),
        0,
      ))
  const viewerIsInAtLeastOneOrganization = !!viewer.organizations.totalCount
  const showLinks =
    !!isSuperAdmin || viewerHasAtLeastOneApp || viewerIsInAtLeastOneOrganization

  const hasIncomingAppRequests =
    !!(viewer.me?.incomingAppRequests || []).length ||
    (viewer.me?.organizations || []).some(
      (organization) => !!(organization.incomingAppRequests || []).length,
    )

  const hasIncomingOrganizationRequests = (viewer.me?.organizations || []).some(
    (organization) =>
      !!organization.ownershipChangeRequest &&
      organization.ownershipChangeRequest.toMember.user.id === viewer.me!.id,
  )

  const hasIncomingOrganizationInvitations = (
    viewer.me?.incomingOrganizationInvitations || []
  ).some(({ rejectedAt, acceptedAt }) => !rejectedAt && !acceptedAt)

  const showNotificationIndicator =
    hasIncomingAppRequests ||
    hasIncomingOrganizationRequests ||
    hasIncomingOrganizationInvitations

  const nameParts = [viewer?.me?.firstName, viewer?.me?.lastName].filter(
    (part): part is string => !!part,
  )
  const hasName = nameParts.length > 0
  const displayName = hasName ? nameParts.join(' ') : 'Missing name'

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={triggerClass}
          data-testid="userMenuButton"
          aria-label={`Account menu for ${displayName}`}
        >
          <span className="relative shrink-0">
            {viewer?.me?.imageUrl && !userImageError && (
              <img
                className="h-5 w-5 rounded-full"
                src={viewer?.me?.imageUrl}
                onError={() => setUserImageError(true)}
                alt={hasName ? `Image of ${displayName}` : 'User avatar'}
                data-testid="userMenuAvatar"
              />
            )}
            {(!viewer?.me?.imageUrl || userImageError) && (
              <span className="bg-radial-[at_75%_25%] block h-5 w-5 rounded-full from-green-300 via-blue-500 to-purple-600" />
            )}
            {showNotificationIndicator && (
              <div
                className="bg-primary absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse rounded-full"
                data-testid="notificationIndicator"
              />
            )}
          </span>
          {!collapsed && (
            <span className="truncate whitespace-nowrap">{displayName}</span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        side="top"
        align="start"
        className="w-64 overflow-visible"
        data-testid="userMenuPanel"
      >
        <div className="px-2 pb-2 pt-1.5">
          <span className="text-foreground-normal block truncate whitespace-nowrap text-base font-medium">
            {displayName}
          </span>
          <span className="text-foreground-subtle block truncate whitespace-nowrap text-sm">
            {viewer?.me?.email}
          </span>
          {windowEnvironment.BUILD_VERSION && (
            <span className="text-foreground-subtle block truncate whitespace-nowrap text-xs">
              Build: {windowEnvironment.BUILD_VERSION}
            </span>
          )}
        </div>

        <DropdownMenu.Separator />

        {showNotificationIndicator && (
          <DropdownMenu.Item asChild>
            <NavLink
              to={paths.notifications()}
              className="group flex justify-between"
            >
              <span>Notifications</span>
              <div
                className="bg-primary block h-2 w-2 animate-pulse rounded-full"
                data-testid="notificationIndicator"
              />
            </NavLink>
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item asChild>
          <NavLink to={paths.settings()}>Settings</NavLink>
        </DropdownMenu.Item>

        <ThemeSwitcher />

        {showLinks && (
          <DropdownMenu.Item asChild>
            <NavLink
              to={paths.createOrganization()}
              className="group flex justify-between"
            >
              <span>New organization</span>
              <Icon className={itemIconClass} svg={<PlusIcon />} />
            </NavLink>
          </DropdownMenu.Item>
        )}

        {isSuperAdmin && (
          <div className="border-border-promo bg-fill-promo-secondary -mx-1.25 z-10 my-1.5 border px-1 py-1.5">
            <DropdownMenu.Item asChild className={superAdminItemClass}>
              <NavLink to={paths.users()}>Users</NavLink>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild className={superAdminItemClass}>
              <NavLink to={paths.findOrganization()}>Find organization</NavLink>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild className={superAdminItemClass}>
              <NavLink to={paths.findApp()}>Find database</NavLink>
            </DropdownMenu.Item>
          </div>
        )}

        {!isSuperAdmin && <DropdownMenu.Separator />}

        <DropdownMenu.Item asChild>
          <a
            href={paths.dittoDocs()}
            className="group flex justify-between"
            target="_blank"
            rel="noreferrer"
          >
            <span>Docs</span>
            <Icon className={itemIconClass} svg={<ArrowUpRightIcon />} />
          </a>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <a
            href={paths.support()}
            className="group flex justify-between"
            target="_blank"
            rel="noreferrer"
          >
            <span>Support</span>
            <Icon className={itemIconClass} svg={<ArrowUpRightIcon />} />
          </a>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <a
            href="https://www.ditto.com/"
            className="group flex justify-between"
            target="_blank"
            rel="noreferrer"
          >
            <span>Ditto homepage</span>
            <Icon className={itemIconClass} svg={<ArrowUpRightIcon />} />
          </a>
        </DropdownMenu.Item>
        {viewer.me?.hubspotContactId && (
          <DropdownMenu.Item asChild>
            <NavLink to={paths.feedback()}>Feedback</NavLink>
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item
          onSelect={() => logout({ returnTo: window.location.origin })}
        >
          Log out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

const themeOptions: { value: Theme; label: string; icon: JSX.Element }[] = [
  { value: 'system', label: 'System', icon: <MonitorIcon /> },
  { value: 'light', label: 'Light', icon: <SunIcon /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon /> },
  {
    value: 'light-high-contrast',
    label: 'Light high contrast',
    icon: <SunIcon weight="fill" />,
  },
  {
    value: 'dark-high-contrast',
    label: 'Dark high contrast',
    icon: <MoonIcon weight="fill" />,
  },
]

/**
 * Segmented control for picking the active theme. Lives in the user menu.
 * Rendered as plain content inside the dropdown (not as a `DropdownMenu.Item`)
 * so that interacting with it doesn't close the menu.
 */
const ThemeSwitcher = () => {
  const { theme, setTheme } = useTheme()

  return (
    <div className="flex items-center justify-between px-2 py-1.5">
      <span className="text-foreground-normal">Theme</span>
      <ButtonGroup role="group" aria-label="Theme">
        {themeOptions.map(({ value, label, icon }) => (
          <Tooltip key={value} tip={label}>
            <Button
              role="menuitemradio"
              aria-checked={theme === value}
              aria-label={label}
              onClick={() => setTheme(value)}
              data-testid={`themeSwitcher-${value}`}
              variant={theme === value ? 'primary' : 'outline'}
              size="square"
            >
              <Icon className="size-4" svg={icon} />
            </Button>
          </Tooltip>
        ))}
      </ButtonGroup>
    </div>
  )
}

export default SideNavUserMenu
